import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";

import {
	ADJUSTMENT_REASON_INITIAL_VALUES,
	CatalogAdjustmentReason,
} from "@/pages/configuration/models/CatalogAdjustmentReason";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	deleteAdjustmentReason,
	upsertAdjustmentReason,
} from "../../../repositories/CatalogAdjustmentReasonRepository";

type Props = {
	adjustmentReason: CatalogAdjustmentReason | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (id?: number) => void;
};

export const UpsertDeleteModal = ({
	adjustmentReason,
	modal,
	onHide,
}: Props) => {
	const [title, setTitle] = useState<string>("CREATE");

	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The adjustment reason was ${modal}d succesfully`;

	const { control, reset, handleSubmit, getValues } =
		useForm<CatalogAdjustmentReason>({
			defaultValues: ADJUSTMENT_REASON_INITIAL_VALUES,
		});

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteAdjustmentReason, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["adjustmentReasons"]);
			setNotificationMessage(succesfullyMessage);
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const upsertMutation = useMutation(upsertAdjustmentReason, {
		onSuccess: async (id: number) => {
			await queryClient.invalidateQueries(["adjustmentReasons"]);
			setNotificationMessage(succesfullyMessage);
			onHide(id);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<CatalogAdjustmentReason> = async (
		data,
		event,
	) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			deleteMutation.mutate(data.AdjustmentReasonId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			setIsSubmitLoading(false);
			reset(adjustmentReason || ADJUSTMENT_REASON_INITIAL_VALUES);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, adjustmentReason]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						adjustmentReason === null
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Adjustment Reason - Adjustment Reasons`}
				/>
			)}
			<MesfModal
				title={`${title} ADJUSTMENT REASON`}
				open={modal !== ""}
				handleClose={() => onHide()}
				maxWidth="sm"
				id="adjustment-reason-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="AdjustmentReasonName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											required
											label="Name"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={modal === "delete"}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									onClick={() => onSubmit(getValues())}
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In AdjustmentReason"
			/>
		</>
	);
};
